import { validationTriggerConfig } from "@features/builder/graph/nodes/validation/validationTriggerConfig"
import { formatValidationTriggerDebugLog } from "@features/builder/graph/nodes/validation/validationTriggerDebug"
import { createValidationTriggerSignature } from "@features/builder/graph/nodes/validation/validationTriggerSignature"
import type { AppNode } from "@features/builder/graph/types"
import type { EditorGraphDto } from "@features/builder/types"
import logger from "@utils/logger"
import { useEffect, useRef } from "react"
import { useDebounceCallback } from "usehooks-ts"

export function useSkipValidation({
  nodes,
  graphDocumentData,
  debounceMs,
  onValidation,
}: {
  nodes: AppNode[]
  graphDocumentData: EditorGraphDto
  debounceMs: number
  onValidation: () => void
}) {
  const previousGraphDataRef = useRef(graphDocumentData.data)
  const previousNodesSignatureRef = useRef<string | null>(null)

  const debouncedValidationCheck = useDebounceCallback(
    (nextNodes: AppNode[], nextGraphDocumentData: EditorGraphDto) => {
      const nodesSignature = createValidationTriggerSignature(
        nextNodes,
        validationTriggerConfig.skipNodePaths,
      )

      const hasRelevantNodesChange = nodesSignature !== previousNodesSignatureRef.current
      const hasRelevantGraphDocumentChange =
        previousGraphDataRef.current !== nextGraphDocumentData.data

      if (import.meta.env.VITE_SHOW_WORKER_VALIDATION_SKIP_LOG === "true") {
        const graphDataChanged = previousGraphDataRef.current !== nextGraphDocumentData.data

        logger.info(
          formatValidationTriggerDebugLog({
            hasRelevantNodesChange,
            hasRelevantGraphDocumentChange,
            graphDataChanged,
          }),
        )
      }

      if (!hasRelevantNodesChange && !hasRelevantGraphDocumentChange) {
        previousGraphDataRef.current = nextGraphDocumentData.data
        return
      }

      previousGraphDataRef.current = nextGraphDocumentData.data
      previousNodesSignatureRef.current = nodesSignature
      onValidation()
    },
    debounceMs,
  )

  useEffect(() => {
    debouncedValidationCheck(nodes, graphDocumentData)
    return () => {
      debouncedValidationCheck.cancel()
    }
  }, [debouncedValidationCheck, graphDocumentData, nodes])
}
